import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { organizationImage } from '@nx-frontend/shared/ui/organization-image';
import { Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { UpgradeTrialIcon } from '@nx-frontend/shared/ui/upgrade-trial-icon';
import { setWorkspaceMenuItemIcon } from '@nx-frontend/shared/ui/workspace-image';
import { goToSettings } from '@nx-frontend/shared/actions';
import { axiosClient, USER_VARIABLE } from '@nx-frontend/shared/api';
import { useCreateFolder } from '@nx-frontend/shared/hooks';
import { useOpenCreateWorkspaceDialog } from '@nx-frontend/shared/hooks/use-create-workspace';
import { useCreateNote } from '@nx-frontend/shared/hooks/use-create/use-note-create';
import { useNewPortalWorkspaceClickAction } from '@nx-frontend/shared/hooks/use-navigate-to-create-portal/use-navigate-to-create-portal';
import { setWorkspaceByIdEvent } from '@nx-frontend/shared/store/current-workspace';
import { setNoteEvent } from '@nx-frontend/shared/store/note';
import { addNoteEvent } from '@nx-frontend/shared/store/notes';
import { $orgPortals } from '@nx-frontend/shared/store/org-portals';
import { $opCanCreatePortal } from '@nx-frontend/shared/store/org-role';
import { $orgUsage } from '@nx-frontend/shared/store/org-usage';
import { $portalsUsage } from '@nx-frontend/shared/store/portal-usage';
import { setSidebarFetchMenuTreeEvent } from '@nx-frontend/shared/store/sidebar';
import { clearBreadcrumbsSpecialPagesVisitedEvent } from '@nx-frontend/shared/store/webnotes-breadcrumbs';
import { $workspacesAvatars } from '@nx-frontend/shared/store/workspace-avatars';
import { $workspaces } from '@nx-frontend/shared/store/workspaces';
import {
  BillingInfo,
  BillingInfoPlan,
  ContextMenuItemData,
  ContextMenuItemType,
  CouponType,
  GetOpenPayWallPopupProps,
  MenuItemDefault,
  MenuItemHtml,
  OpenModalFn,
  Org,
  OrganizationPrivilege,
  OrgCoupons,
  OrgInfo,
  OrgInfoSubscriptionType,
  OrgMemberRole,
  OrgMemberRoleAndPrivileges,
  PaywallPopupPayload,
  PaywallPopupPricingReason,
  PaywallPopupReason,
  PopupType,
  PortalServicePortalDto,
  TranslatesFnType,
  TrialServiceBusinessTrialDto,
  UiContextMenuConfigPosition,
  UiContextMenuTheme,
  UiTooltipPosition,
  UnleashFeatures,
  UnleashFlags,
  User,
  Workspace,
  WorkspaceAvatar,
} from '@nx-frontend/shared/types';
import {
  getBoxFileUrl,
  getFirstLetters,
  getPaywallTitle,
  hasAppsumoBundle,
  hasMaxSubscriptionPlan,
  NextRouterOptions,
} from '@nx-frontend/shared/utils';

const tooltipContent = {
  user: 'Nimbus Web Inc',
};
const tooltipContentLocKeys = {
  plus: 'webnotes.webnotes-add_pages_folders_ect',
  lock: 'webnotes.webnotes-private_workspace',
  edit: 'webnotes.webnotes-alpha_workspace',
  invite: 'webnotes.webnotes-invite_members_and_clients',
  productUpdates: 'webnotes.webnotes-product_updates',
  settings: 'webnotes.webnotes-settings',
  orgSettings: 'webnotes.webnotes-organization_settings',
  profileSettings: 'webnotes.webnotes-profile_settings',
  logout: 'webnotes.webnotes-logout',
  upgrade: 'webnotes.webnotes-upgrade_your_plan',
};

// Expects global translations scope
export const getButtonsUpperBlock = (
  t: TranslatesFnType,
  organization: Org,
  role: OrgMemberRoleAndPrivileges,
  path: string,
  currentWorkspaceId: string,
  portalId: string,
  workspaces: Workspace[],
  workspaceAvatarsByGlobalId: Record<string, WorkspaceAvatar>,
  maxVisibleBtns: number,
  workspaceListAvailable: boolean
) => {
  const block: {
    id: string;
    icon: ReactElement | null;
    image: string | null;
    tooltipContent?: string;
    isSelected?: boolean;
    hidden?: boolean;
  }[] = [];

  const wrapperClass = 'w-8 h-8 bg-white border border-neutral-200 rounded flex items-center justify-center';
  block.push({
    id: 'org',
    icon: null,
    image: getOrganizationTopIcon(organization),
    tooltipContent: organization.title,
  });
  block.push({
    id: 'plus',
    icon: <Icon icon={IconsMap.PLUS} className="text-neutral-500 w-5 h-5" />,
    image: null,
    tooltipContent: t(tooltipContentLocKeys.plus),
  });
  const sortedWorkspaces = [...workspaces].sort((a, b) => {
    if (a.globalId === currentWorkspaceId) return -1;
    if (b.globalId === currentWorkspaceId) return 1;
    return 0;
  });
  for (const workspace of sortedWorkspaces) {
    const workspaceAvatar: WorkspaceAvatar | undefined = workspaceAvatarsByGlobalId[workspace.globalId];
    const workspaceAvatarUrl = workspaceAvatar?.storedFileUUID ? getBoxFileUrl(workspaceAvatar?.storedFileUUID) : null;
    const wsLetters = getFirstLetters(workspace.title)?.toUpperCase();
    if (!(!workspaceListAvailable && workspace.globalId !== currentWorkspaceId)) {
      block.push({
        id: `workspace_${workspace.globalId}`,
        icon: !workspaceAvatarUrl ? <div className={classNames(wrapperClass, 'text-xs')}>{wsLetters}</div> : null,
        image: workspaceAvatarUrl,
        tooltipContent: workspace ? workspace.title : t(tooltipContentLocKeys.edit),
        hidden: !(block.length < maxVisibleBtns - 1) && workspace.globalId !== currentWorkspaceId,
        isSelected:
          currentWorkspaceId === workspace.globalId &&
          (path.startsWith(`/space/${workspace.globalId}`) || path.startsWith(`/portal/${portalId}`)),
      });
    }
  }

  if (role.privileges.includes(OrganizationPrivilege.CAN_MANAGE_AUTOMATION)) {
    block.push({
      id: 'automation',
      icon: <Icon icon={IconsMap.AUTOMATION} className="text-neutral-500 w-5 h-5" />,
      image: null,
      tooltipContent: t('webnotes.webnotes-automation'),
      isSelected: path.startsWith('/space/automation'),
    });
  }

  block.push({
    id: 'ai-agents',
    icon: <Icon icon={IconsMap.AI_AGENT_ROBOT} className="text-neutral-500 w-5 h-5" />,
    image: null,
    tooltipContent: t('webnotes.webnotes-ai_agents'),
  });

  return block;
};

// Expects global translations scope
export const getButtonsLowerBlock = (
  t: TranslatesFnType,
  organization: Org,
  user: User,
  role: OrgMemberRoleAndPrivileges,
  path: string,
  orgCoupons: OrgCoupons,
  billingInfo: BillingInfo,
  orgInfo: OrgInfo,
  isBusinessTrialActive: boolean,
  businessTrial: TrialServiceBusinessTrialDto | null | undefined,
  isLoaded: boolean,
  pathname: string,
  unleashFlags: UnleashFlags,
  canAccessOrgPanel: boolean
) => {
  const block: {
    id: string;
    icon: ReactElement | null;
    image: string | null;
    tooltipContent?: string;
    additionalTooltipContent?: string;
    isSelected?: boolean;
    trialDaysLeft?: number;
  }[] = [];

  const isMaxSubscriptionPlan = hasMaxSubscriptionPlan(orgInfo, billingInfo, orgCoupons);
  const inviteSelected = path.startsWith('/space/settings') && path.includes('/members/invite');
  if (
    typeof pathname === 'string' &&
    -1 === pathname.indexOf('/portal/') &&
    isLoaded &&
    role.privileges.includes(OrganizationPrivilege.CAN_UPDATE_ORG) &&
    !isMaxSubscriptionPlan &&
    0 === Object.keys(orgCoupons.couponNumber).length
  ) {
    if (isBusinessTrialActive && businessTrial) {
      const secondsInDay = 86400;
      const now = Math.ceil(new Date().getTime() / 1000);
      const days = Math.ceil((businessTrial.dateUntil - now) / secondsInDay);
      block.push({
        id: 'upgrade-trial',
        icon: <UpgradeTrialIcon dateUntil={businessTrial.dateUntil} />,
        image: null,
        tooltipContent: t(tooltipContentLocKeys.upgrade),
        additionalTooltipContent: '',
        trialDaysLeft: days,
      });
    } else {
      const now = Math.ceil(new Date().getTime() / 1000);
      if (0 === billingInfo.trialEndsAt || now > billingInfo.trialEndsAt) {
        block.push({
          id: 'upgrade',
          icon: <Icon icon={IconsMap.UPGRADE2} className="w-32 h-32" />,
          image: null,
          tooltipContent: t(tooltipContentLocKeys.upgrade),
          additionalTooltipContent: '',
        });
      }
    }
  }

  if (unleashFlags[UnleashFeatures.BEAMER] && role.role === OrgMemberRole.OWNER) {
    block.push({
      id: 'beamer',
      icon: (
        <div className="h-8 w-8 text-[10px] font-medium bg-cyan-100 text-cyan-700 flex items-center justify-center rounded">
          <span>NEW</span>
        </div>
      ),
      tooltipContent: t(tooltipContentLocKeys.productUpdates),
      image: null,
    });
  }

  if (role.privileges.includes(OrganizationPrivilege.CAN_ADD_MEMBERS)) {
    block.push({
      id: 'invite',
      icon: <Icon icon={IconsMap.USER_PLUS} className="text-neutral-500 w-5 h-5" />,
      image: null,
      tooltipContent: t(tooltipContentLocKeys.invite),
      additionalTooltipContent: organization ? organization.title : '',
      isSelected: inviteSelected,
    });
  }

  // show academy only for owner
  if (role.role === OrgMemberRole.OWNER) {
    block.push({
      id: 'academy',
      icon: <Icon icon={IconsMap.STUDENT2} className="text-neutral-500 w-5 h-5" />,
      image: null,
      tooltipContent: t('webnotes.webnotes-fusebase_academy'),
      additionalTooltipContent: '',
      isSelected: false,
    });
  }

  if (canAccessOrgPanel) {
    block.push({
      id: 'settings',
      icon: <Icon icon={IconsMap.COG} className="text-neutral-500 w-5 h-5" />,
      image: null,
      tooltipContent: t(tooltipContentLocKeys.settings),
      additionalTooltipContent: '',
      isSelected: path.startsWith('/space/settings') && !inviteSelected,
    });
  }

  const userImage = getUserImage(user);
  const userIcon = getUserIcon();
  block.push({
    id: 'user',
    icon: userImage ? null : userIcon,
    image: userImage,
    tooltipContent: getUserName(user),
    additionalTooltipContent: organization ? organization.title : '',
  });

  return block;
};

const getUserName = (user: User) => {
  if (!user) {
    return tooltipContent.user;
  }

  let name = '';
  if (user.firstname || user.lastname) {
    if (user.firstname) {
      name += user.firstname;
    }

    if (user.lastname) {
      if (name) {
        name += ' ';
      }
      name += user.lastname;
    }
  }

  return name.trim() ? name : user.email;
};

const getUserImage = (user: User) => {
  if (!user) {
    return null;
  }
  if (!user.avatar) {
    return null;
  }
  return getBoxFileUrl(user.avatar, { width: 64, height: 64 });
};

const getUserIcon = () => {
  return (
    <div className="rounded-full bg-neutral-800 w-8 h-8 flex items-center justify-center">
      <Icon icon={IconsMap.PERSON_FILL} className="text-white w-6 h-6" />
    </div>
  );
};

const getOrganizationTopIcon = (organization: Org) => {
  const defaultIcon = '/assets/img/organization.svg';
  if (!organization) {
    return defaultIcon;
  }
  if (!organization.smallLogoStoredFileUUID) {
    return defaultIcon;
  }
  return getBoxFileUrl(organization.smallLogoStoredFileUUID, { width: 64, height: 64 });
};

const newPageMenuItem: ContextMenuItemData = {
  id: 'new-page',
  title: 'New Page',
  icon: <Icon icon={IconsMap.DOCUMENT} className="text-neutral-500" />,
  type: ContextMenuItemType.DEFAULT,
};
Object.freeze(newPageMenuItem);

const newFolderMenuItem: ContextMenuItemData = {
  id: 'new-folder',
  title: 'New Folder',
  icon: <Icon icon={IconsMap.FOLDER} className="text-neutral-500" />,
  type: ContextMenuItemType.DEFAULT,
  isSeparator: true,
};
Object.freeze(newFolderMenuItem);

const newPortalMenuItem: ContextMenuItemData = {
  title: 'New Portal',
  icon: <Icon icon={IconsMap.PORTAL_8} className="text-neutral-500" />,
  type: ContextMenuItemType.DEFAULT,
};
Object.freeze(newPortalMenuItem);

const newWorkspaceMenuItem: ContextMenuItemData = {
  title: 'Add Workspace',
  icon: <Icon icon={IconsMap.WORKSPACES} className="text-neutral-500" />,
  type: ContextMenuItemType.DEFAULT,
};
Object.freeze(newWorkspaceMenuItem);

const workspaceSelectMenuItem: ContextMenuItemData = {
  type: ContextMenuItemType.DEFAULT,
  title: 'My Workspace',
  icon: <Icon icon={IconsMap.LOCK} />,
};
Object.freeze(workspaceSelectMenuItem);

const innerSpaceMenuItem: ContextMenuItemData = {
  type: ContextMenuItemType.DEFAULT,
  title: 'Internal Space',
  icon: <Icon icon={IconsMap.LOCK} />,
};
Object.freeze(innerSpaceMenuItem);

const portalMenuItem: ContextMenuItemData = {
  type: ContextMenuItemType.DEFAULT,
  title: 'Blueprint Portal',
  icon: <Icon icon={IconsMap.PORTAL_8} />,
};
Object.freeze(portalMenuItem);

// Expects global localization scope
export const getMenuUserButton = (
  t: TranslatesFnType,
  currentOrganization: Org,
  organizations: Org[],
  router: NextRouterOptions,
  currentWorkspaceGlobalId: string,
  currentNoteGlobalId: string,
  openModal: OpenModalFn,
  orgCoupons: OrgCoupons,
  billingInfo: BillingInfo,
  orgInfo: OrgInfo,
  user: User,
  businessTrial: TrialServiceBusinessTrialDto | null | undefined,
  pathname: string,
  showZohoChat: () => void,
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void,
  goToAppsumoBundlePurchase: (orgId: string) => void,
  logout: () => void,
  userId?: number
) => {
  const menuBlock: ContextMenuItemData[] = [];

  const handleLogout = async () => {
    logout();
  };

  const getOrganizationUrl = (organization: Org) => {
    const defaultUrl = window.location.href;
    if (!organization) {
      return defaultUrl;
    }
    return '/auth/open-org/' + organization.globalId;
  };

  const openSettings = () => {
    router.routerPushFn(`/space/${currentWorkspaceGlobalId}/settings`);
  };

  menuBlock.push({
    type: ContextMenuItemType.HTML,
    html: (
      <div data-cy="current-organization" className="flex flex-col items-start w-full">
        <div className="w-full flex flex-row gap-3 items-center">
          <div
            onClick={() => (window.location.href = getOrganizationUrl(currentOrganization))}
            className="flex flex-row w-full justify-between items-center gap-3 py-0.5"
          >
            <div className="flex gap-2 items-center">
              {organizationImage(currentOrganization, { addStyles: 'rounded-full' })}
              <span className="text-sm max-w-[200px] truncate font-bold">{currentOrganization.title}</span>
            </div>
          </div>
          <Tooltip message={t(tooltipContentLocKeys.orgSettings)} position={UiTooltipPosition.TOP}>
            <span
              data-cy="org-settings"
              className="hover:bg-neutral-200 hover:bg-opacity-300 hover:rounded relative transition duration-500"
              onClick={() => goToSettings(router, '/org/' + currentOrganization.globalId)}
            >
              <Icon icon={IconsMap.COG} className="text-neutral-500 w-4 h-4" />
            </span>
          </Tooltip>
        </div>
        {/* <div data-cy="add-cname" className="flex flex-row items-center w-full justify-start ml-8 gap-2 py-0.5">
        <span className="text-sm text-neutral-500 ">://</span>
        <span className="text-cyan-500 text-[13px]">Add CNAME</span>
      </div> */}
      </div>
    ),
    additionalStyles: 'mt-2 min-w-[250px]',
    isSeparator: true,
  });

  const otherOrganizations = organizations.filter(
    (organization) => organization.globalId !== currentOrganization.globalId
  );

  otherOrganizations.forEach((organization, index) => {
    const isFirstOrganization = index === 0;
    const isLastOrganization = index === otherOrganizations.length - 1;
    menuBlock.push({
      type: ContextMenuItemType.HTML,
      html: (
        <div
          data-cy={organization.title}
          onClick={async () => {
            try {
              if (userId && organization.globalId) {
                await axiosClient.post(
                  `/v2/api/users/vars/${USER_VARIABLE.LAST_OPENED_ORG}`,
                  {
                    value: organization.globalId,
                  },
                  { userId }
                );
              }
            } catch (err) {
              //do nothing
            }
            window.location.href = getOrganizationUrl(organization);
          }}
          className={classNames('flex flex-row w-full justify-between items-center pt-1 pb-1', {
            'pt-2': isFirstOrganization,
            'pb-2': isLastOrganization,
          })}
        >
          <div className="flex gap-2 items-center">
            {organizationImage(organization, { addStyles: 'rounded-full' })}
            <span className="text-sm max-w-[200px] truncate">{organization.title}</span>
          </div>
        </div>
      ),
      isSeparator: isLastOrganization,
    });
  });

  if (user.id === currentOrganization.userId) {
    menuBlock.push({
      id: 'chat-with-us',
      type: ContextMenuItemType.HTML,
      html: (
        <div className="flex flex-row justify-start gap-2 items-center pt-1">
          <Icon icon={IconsMap.CHAT} className="text-cyan-900 w-4 h-4" />
          <span className="text-sm">{t('webnotes.webnotes-message_support')}</span>
        </div>
      ),
      action: () => {
        showZohoChat();
      },
    });
  }

  // menuBlock.push({
  //   type: ContextMenuItemType.HTML,
  //   html: (
  //     <div className="flex flex-row justify-start gap-2 items-center">
  //       <Icon icon={IconsMap.ESTIMATED_TIME} className="text-cyan-500 w-4 h-4" />
  //       <span className="text-sm">Business Trial</span>
  //     </div>
  //   ),
  // });

  const showAppsumoBundleBanner = hasAppsumoBundle(billingInfo, orgCoupons);
  // Whether user has the more advanced(max) plan and can not upgrade to a higher plan
  const isMaxSubscriptionPlan = hasMaxSubscriptionPlan(orgInfo, billingInfo, orgCoupons);

  let shouldShowUpgradePlan = false;
  // only org owner can see the upgrade plan button
  if (user.id === currentOrganization.userId) {
    if (
      orgCoupons.isLoaded &&
      (orgCoupons.couponNumber[CouponType.APPSUMO2023] > 0 || orgCoupons.couponNumber[CouponType.APPSUMO2020] > 0)
    ) {
      if (
        billingInfo &&
        billingInfo.plan &&
        billingInfo.active &&
        billingInfo.plan === BillingInfoPlan.APPSUMO_SUBSCRIPTION_2023
      ) {
        // do nothing
      } else {
        shouldShowUpgradePlan = true;
      }
    }

    if (!shouldShowUpgradePlan) {
      if (
        [
          OrgInfoSubscriptionType.BUSINESS,
          OrgInfoSubscriptionType.BUSINESS2023,
          OrgInfoSubscriptionType.BUSINESS2024,
        ].includes(orgInfo.subscriptionType) === false ||
        billingInfo.plan === BillingInfoPlan.TRIAL
      ) {
        // still has room for upgrade, because business is the max plan
        shouldShowUpgradePlan = true;
      }
    }
  }

  if (shouldShowUpgradePlan) {
    let itemText = t('webnotes.webnotes-upgrade_for_more_powers');
    let paywallTitle = getPaywallTitle(orgInfo.subscriptionType, t);

    if (orgInfo.subscriptionType === 'team' && orgCoupons.isLoaded && Object.keys(orgCoupons.couponNumber).length) {
      paywallTitle = t('features.paywall.title_boost_client_experience');
    }

    if (billingInfo.plan === BillingInfoPlan.TRIAL && businessTrial && businessTrial.state === 'active') {
      // include number of days until trial ends in the item text
      const daysLeft = Math.ceil((businessTrial.dateUntil - Date.now() / 1000) / 3600 / 24);
      itemText = t('webnotes.webnotes-upgrade_trial_days_left', { daysLeft: daysLeft.toString() });
    }

    if (typeof pathname === 'string' && -1 === pathname.indexOf('/portal/')) {
      menuBlock.push({
        id: 'upgrade-plan',
        type: ContextMenuItemType.HTML,
        html: (
          <div className="flex flex-row justify-start gap-2 items-center">
            <Icon icon={IconsMap.STAR} className="text-cyan-900 w-4 h-4" />
            <span className="text-sm">{itemText}</span>
          </div>
        ),
        action: () => {
          if (isMaxSubscriptionPlan && showAppsumoBundleBanner) {
            // redirect to appsumo bundle straight away
            goToAppsumoBundlePurchase(currentOrganization.globalId);
          } else {
            const paywallPopupPayload: PaywallPopupPayload = {
              payloadType: PopupType.PAYWALL,
              title: paywallTitle,
              reason: PaywallPopupReason.MENU_UPGRADE_TO_PRO,
              pricingReason: PaywallPopupPricingReason.MENU_UPGRADE_TO_PRO,
              orgId: currentOrganization.globalId,
            };

            getOpenPaywallPopup({ openModal, payload: paywallPopupPayload });
          }
        },
      });
    }
  }

  if (user.id === currentOrganization.userId) {
    // showing FusebaseHelper only for the owner of the organization
    menuBlock.push({
      id: 'fusebase-helper',
      type: ContextMenuItemType.HTML,
      html: (
        <div className="flex flex-row justify-start gap-2 items-center">
          <Icon icon={IconsMap.QUESTION} className="text-cyan-900 w-4 h-4" />
          <span className="text-sm">{t('webnotes.webnotes-help_and_docs')}</span>
        </div>
      ),
      action: () => {
        if (window.userpilot) {
          window.userpilot.trigger('resource_center:oLZEeJ0mak');
        } else {
          console.error('Userpilot is not available to trigger Fusebase Helper');
        }
      },
    });
  }

  menuBlock.push({
    id: 'get-expert-demo',
    type: ContextMenuItemType.HTML,
    html: (
      <div className="flex flex-row justify-start gap-2 items-center pb-1">
        <Icon icon={IconsMap.CALENDAR} className="text-cyan-900 w-4 h-4" />
        <span className="text-sm">{t('webnotes.webnotes-get_expert_demo')}</span>
      </div>
    ),
    action: () => {
      window.open(`https://thefusebase.com/support/`);
    },
    isSeparator: true,
  });

  menuBlock.push({
    id: 'switch-to-old-client',
    type: ContextMenuItemType.HTML,
    html: (
      <div
        className="flex flex-row justify-start gap-2 items-center pb-1"
        onClick={async () => {
          try {
            if (userId) {
              await axiosClient.post(
                `/v2/api/users/vars/${USER_VARIABLE.USE_OLD_CLIENT}`,
                {
                  value: true,
                },
                { userId }
              );
            }
          } catch (err) {
            //do nothing
          }
          let url = 'https://' + document.location.host + '/ws';
          if (currentWorkspaceGlobalId) {
            url += '/' + currentWorkspaceGlobalId;
            if (currentNoteGlobalId) {
              url += '/recent/note/' + currentNoteGlobalId;
            } else {
              url += '/recent';
            }
          }

          document.location.href = url;
        }}
      >
        <Icon icon={IconsMap.SWITCH} className="text-neutral-500 w-4 h-4" />
        <div className="flex flex-col items-start">
          <span className="text-sm">{t('webnotes.webnotes-switch_to_old_version')}</span>
          <span className="text-[10px] whitespace-pre-line text-neutral-500 leading-[12px] w-[170px]">
            {t('webnotes.webnotes-previous_version_any_time')}
          </span>
        </div>
      </div>
    ),
    isSeparator: true,
  });

  menuBlock.push({
    type: ContextMenuItemType.HTML,
    additionalStyles: 'hover:bg-transparent !cursor-auto',
    html: (
      <div className="flex flex-row w-full justify-between gap-2 items-center pb-1">
        <span className="max-w-[200px] truncate">
          {user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : `${user.email}`}
        </span>
        <div className="flex gap-2">
          {currentWorkspaceGlobalId ? (
            <Tooltip message={t(tooltipContentLocKeys.profileSettings)} position={UiTooltipPosition.TOP}>
              <span
                data-cy="profile-settings"
                className="hover:bg-neutral-200 hover:bg-opacity-300 hover:rounded relative transition duration-500 cursor-pointer"
                onClick={openSettings}
              >
                <Icon icon={IconsMap.COG} className="text-neutral-500 w-4 h-4" />
              </span>
            </Tooltip>
          ) : null}
          <Tooltip message={t(tooltipContentLocKeys.logout)} position={UiTooltipPosition.TOP}>
            <span
              onClick={handleLogout}
              className="cursor-pointer hover:bg-neutral-200 hover:bg-opacity-300 hover:rounded transition duration-500"
            >
              <Icon icon={IconsMap.LOG_OUT} className="text-neutral-500 w-4 h-4" />
            </span>
          </Tooltip>
        </div>
      </div>
    ),
  });

  return menuBlock;
};

export const useOpenNavbarPlusMenu = () => {
  const t = useTranslations('webnotes');
  const [workspaces, workspacesAvatars, orgUsage, portalsUsage, orgPortals, opCanCreatePortal] = useUnit([
    $workspaces,
    $workspacesAvatars,
    $orgUsage,
    $portalsUsage,
    $orgPortals,
    $opCanCreatePortal,
  ]);
  const [
    setNote,
    addNote,
    setWorkspaceById,
    setSidebarFetchMenuTree,
    clearBreadcrumbsSpecialPagesVisited,
    openContextMenu,
  ] = useEvent([
    setNoteEvent,
    addNoteEvent,
    setWorkspaceByIdEvent,
    setSidebarFetchMenuTreeEvent,
    clearBreadcrumbsSpecialPagesVisitedEvent,
    openContextMenuEvent,
  ]);
  const createNote = useCreateNote();
  const createFolder = useCreateFolder();
  const openCreateWorkspaceDialog = useOpenCreateWorkspaceDialog();
  const newPortalWorkspaceClickAction = useNewPortalWorkspaceClickAction();

  const workspacesAvatarsByGlobalId = useMemo(() => {
    return workspacesAvatars.reduce((acc, avatar) => {
      acc[avatar.workspaceId] = avatar;
      return acc;
    }, {} as Record<string, WorkspaceAvatar>);
  }, [workspacesAvatars]);

  return useCallback(
    (target: HTMLElement | EventTarget) => {
      const newPageItem = Object.assign({}, newPageMenuItem, { title: t('webnotes-new_page') });
      const newFolderItem = Object.assign({}, newFolderMenuItem, { title: t('webnotes-new_folder') });

      const getCreateNoteInInnerSpaceFn = (workspace: Workspace) => {
        return async () => {
          const note = await createNote({
            workspaceId: workspace.globalId,
            parentId: 'default',
            redirectAfterCreate: true,
          });
          if (note) {
            addNote(note);
            setNote({ workspaceId: note.workspaceId, noteId: note.globalId });
            setWorkspaceById(note.workspaceId);
            setSidebarFetchMenuTree(true);
            clearBreadcrumbsSpecialPagesVisited();
          }
        };
      };

      const getCreateFolderInInnerSpaceFn = (workspace: Workspace) => {
        return async () => {
          setWorkspaceById(workspace.globalId);
          createFolder({
            parentId: 'root',
            workspaceId: workspace.globalId,
          });
        };
      };

      let newPageItemInitialized = false;
      let newFolderItemInitialized = false;

      if (workspaces.length === 1) {
        newPageItem.action = getCreateNoteInInnerSpaceFn(workspaces[0]);
        newFolderItem.action = getCreateFolderInInnerSpaceFn(workspaces[0]);
        newPageItemInitialized = true;
        newFolderItemInitialized = true;
      }

      if (!newPageItemInitialized) {
        newPageItem.subMenu = workspaces.map((workspace) => {
          const workspaceItem: MenuItemDefault = Object.assign({}, workspaceSelectMenuItem, {
            title: t('webnotes-my_workspace'),
          }) as MenuItemDefault;
          setWorkspaceMenuItemIcon(workspaceItem, workspace, workspacesAvatarsByGlobalId[workspace.globalId], 'small');
          workspaceItem.title = workspace.title;
          // TODO creation of notes in portals
          const workspacePortal = null;
          if (workspacePortal) {
            const innerSpaceItem = Object.assign({}, innerSpaceMenuItem, { title: t('webnotes-internal_space') });
            innerSpaceItem.action = getCreateNoteInInnerSpaceFn(workspace);
            workspaceItem.subMenu = [innerSpaceItem];
          } else {
            workspaceItem.action = getCreateNoteInInnerSpaceFn(workspace);
          }
          return workspaceItem;
        });
      }

      if (!newFolderItemInitialized) {
        newFolderItem.subMenu = workspaces.map((workspace) => {
          const workspaceItem: MenuItemDefault = Object.assign({}, workspaceSelectMenuItem, {
            title: t('webnotes-my_workspace'),
          }) as MenuItemDefault;
          setWorkspaceMenuItemIcon(workspaceItem, workspace, workspacesAvatarsByGlobalId[workspace.globalId], 'small');
          workspaceItem.title = workspace.title;
          // TODO creation of folders in portals
          const workspacePortal = null;
          if (workspacePortal) {
            const innerSpaceItem = Object.assign({}, innerSpaceMenuItem, { title: t('webnotes-internal_space') });
            innerSpaceItem.action = getCreateFolderInInnerSpaceFn(workspace);
            workspaceItem.subMenu = [innerSpaceItem];
          } else {
            workspaceItem.action = getCreateFolderInInnerSpaceFn(workspace);
          }

          return workspaceItem;
        });
      }

      const newWorkspaceItem = Object.assign({}, newWorkspaceMenuItem, { title: t('webnotes-new_workspace') });
      newWorkspaceItem.action = () => {
        openCreateWorkspaceDialog();
      };

      const newPortalItem = Object.assign({}, newPortalMenuItem, { title: t('webnotes-new_portal') });

      if (workspaces) {
        const activeWorkspaces = orgUsage.data?.workspaces?.current;
        let canAddNewPortal = false;
        if (opCanCreatePortal && activeWorkspaces && activeWorkspaces <= portalsUsage.active) {
          canAddNewPortal = true;
        }
        if (workspaces.length > 1 || canAddNewPortal) {
          newPortalItem.subMenu = workspaces.map((workspace) => {
            let workspacePortal: PortalServicePortalDto | undefined = undefined;
            if (orgPortals && orgPortals.length) {
              workspacePortal = orgPortals.find((portal) => portal.workspaceId === workspace.globalId);
            }
            const workspaceItem: MenuItemDefault = Object.assign({}, workspaceSelectMenuItem, {
              title: t('webnotes-my_workspace'),
            }) as MenuItemDefault;
            setWorkspaceMenuItemIcon(
              workspaceItem,
              workspace,
              workspacesAvatarsByGlobalId[workspace.globalId],
              'small'
            );
            workspaceItem.title = workspace.title;
            workspaceItem.action = () =>
              newPortalWorkspaceClickAction({
                workspace,
              });
            if (workspacePortal) {
              workspaceItem.disabled = true;
              workspaceItem.tooltip = t('webnotes-workspace_already_has_portal');
              workspaceItem.icon = <Icon icon={IconsMap.LOCK} />;
            }
            workspaceItem.subMenu = undefined;
            return workspaceItem;
          });
          if (canAddNewPortal) {
            const menuItem: MenuItemDefault = Object.assign({}, workspaceSelectMenuItem, {
              title: t('webnotes-my_workspace'),
            }) as MenuItemDefault;
            menuItem.title = t('webnotes-add_new_portal');
            menuItem.subMenu = undefined;
            menuItem.icon = <Icon icon={IconsMap.PLUS_GRADIENT} />;
            menuItem.textStyleClasses = 'add-new-portal-gradient font-bold text-[13px]';
            menuItem.iconStyles = 'add-new-portal-gradient';
            menuItem.action = () =>
              newPortalWorkspaceClickAction({
                workspace: null,
              });
            newPortalItem.subMenu.push(menuItem);
          }
        } else if (workspaces.length === 1) {
          newPortalItem.action = () =>
            newPortalWorkspaceClickAction({
              workspace: workspaces[0],
            });
        }
      }

      const pathname = window.location.pathname;
      const items: (MenuItemDefault | MenuItemHtml)[] = [];

      items.push(newPageItem);
      if (
        pathname.indexOf('/space/') === 0 ||
        pathname.indexOf('/settings/') === 0 ||
        pathname.indexOf('/space/settings/') === 0 ||
        pathname.indexOf('/dashboard/') === 0 ||
        pathname.indexOf('/portal/') === 0
      ) {
        items.push(newPortalItem);
      }
      items.push(newFolderItem);
      items.push(newWorkspaceItem);

      openContextMenu({
        items,
        position: calculateContextMenuPosition(target, 0, UiContextMenuConfigPosition.TOP_RIGHT),
        config: {
          theme: UiContextMenuTheme.LIGHT,
        },
        usePopperSubmenu: true,
      });
    },
    [
      addNote,
      clearBreadcrumbsSpecialPagesVisited,
      createFolder,
      createNote,
      newPortalWorkspaceClickAction,
      opCanCreatePortal,
      openContextMenu,
      openCreateWorkspaceDialog,
      orgPortals,
      orgUsage.data?.workspaces,
      portalsUsage.active,
      setNote,
      setSidebarFetchMenuTree,
      setWorkspaceById,
      t,
      workspaces,
      workspacesAvatarsByGlobalId,
    ]
  );
};
